import {
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import type {
    ColumnDef,
    PaginationState,
    SortingState,
} from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useState } from 'react';
import Icon from '@components/common/Icon';
import SkeletonLoader from '@components/common/SkeletonLoader';

// ─── Types ─────────────────────────────────────────────────────────────────
interface DataGridTableProps<TData> {
    data: TData[];
    columns: ColumnDef<TData, unknown>[];
    totalCount?: number;
    pageIndex?: number;
    pageSize?: number;
    onPageChange?: (pageIndex: number) => void;
    loading?: boolean;
    /** Number of skeleton rows when `loading` (defaults to `pageSize`). */
    skeletonRows?: number;
    /** When true, the pagination bar is omitted (e.g. inline tables that show all rows). */
    hidePagination?: boolean;
    /** Rendered below the table, inside the same card (e.g. total row). */
    footer?: ReactNode;
    /** Keep the last column visible while horizontally scrolling. */
    stickyLastColumn?: boolean;
    /** Keep header row visible while scrolling inside the table container. */
    stickyHeader?: boolean;
    /** Max height for the table scroll area when sticky header is enabled. */
    maxTableHeight?: number | string;
}

// ─── Sort Icon ──────────────────────────────────────────────────────────────
const SortIcon = ({ direction }: { direction: 'asc' | 'desc' | false }) => (
    <span className="inline-flex flex-col ml-1 opacity-40 group-hover:opacity-80 transition-opacity">
        <Icon
            name="sortAsc"
            size={8}
            alt=""
            className={direction === 'asc' ? 'opacity-100' : 'opacity-30'}
        />
        <Icon
            name="sortDesc"
            size={8}
            alt=""
            className={`${direction === 'desc' ? 'opacity-100' : 'opacity-30'} mt-[1px]`}
        />
    </span>
);


// ─── Pagination ─────────────────────────────────────────────────────────────
const Pagination = ({
    pageIndex,
    pageCount,
    totalCount,
    pageSize,
    onPageChange,
}: {
    pageIndex: number;
    pageCount: number;
    totalCount: number;
    pageSize: number;
    onPageChange: (p: number) => void;
}) => {
    const from = totalCount === 0 ? 0 : pageIndex * pageSize + 1;
    const to = totalCount === 0 ? 0 : Math.min((pageIndex + 1) * pageSize, totalCount);

    const pages: (number | '...')[] = [];
    if (pageCount <= 7) {
        for (let i = 0; i < pageCount; i++) pages.push(i);
    } else {
        pages.push(0);
        if (pageIndex > 2) pages.push('...');
        const start = Math.max(1, pageIndex - 1);
        const end = Math.min(pageCount - 2, pageIndex + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        if (pageIndex < pageCount - 3) pages.push('...');
        pages.push(pageCount - 1);
    }

    return (
        <div className="flex items-center justify-between px-3 2xl:px-4 py-2 2xl:py-3 border-t border-[var(--neutral-color)]">
            <p className="text-xs 2xl:text-sm text-[var(--disabled-text-color)]">
                Showing{' '}
                <span className="font-medium text-[var(--secondary-color)]">{from}–{to}</span>
                {' '}of <span className="font-medium text-[var(--secondary-color)]">{totalCount}</span> records
            </p>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPageChange(pageIndex - 1)}
                    disabled={pageIndex === 0}
                    aria-label="Previous page"
                    className="w-6 2xl:w-8 h-6 2xl:h-8 flex items-center justify-center rounded-[6px] 2xl:rounded-[10px] border border-[var(--neutral-color)] hover:bg-[var(--primary-surface-color)] disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed transition-colors"
                >
                    <Icon name="chevronLeft" className='w-[14px] 2xl:w-[17px] h-auto' alt="" />
                </button>

                {pages.map((p, i) =>
                    p === '...' ? (
                        <span key={`ellipsis-${i}`} className="w-7 h-7 flex items-center justify-center text-xs text-gray-400 ">…</span>
                    ) : (
                        <button
                            key={p}
                            onClick={() => onPageChange(p as number)}
                            className={`w-6 2xl:w-8 h-6 2xl:h-8 flex items-center justify-center rounded-[6px] 2xl:rounded-[10px] text-xs 2xl:text-sm font-medium transition-colors cursor-pointer ${p === pageIndex
                                ? 'bg-[var(--primary-color)] text-white'
                                : 'border border-[var(--neutral-color)] text-[var(--secondary-color)] hover:bg-[var(--primary-surface-color)]'
                                }`}
                        >
                            {(p as number) + 1}
                        </button>
                    )
                )}

                <button
                    onClick={() => onPageChange(pageIndex + 1)}
                    disabled={pageIndex >= pageCount - 1}
                    aria-label="Next page"
                    className="w-6 2xl:w-8 h-6 2xl:h-8 flex items-center justify-center rounded-[6px] 2xl:rounded-[10px] border border-[var(--neutral-color)] hover:bg-[var(--primary-surface-color)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                    <Icon name="chevronRight" className='w-[14px] 2xl:w-[17px] h-auto' alt="" />
                </button>
            </div>
        </div>
    );
};

// ─── DataGridTable ──────────────────────────────────────────────────────────
const DataGridTable = <TData,>({
    data,
    columns,
    totalCount,
    pageIndex: controlledPageIndex,
    pageSize: controlledPageSize = 10,
    onPageChange,
    loading = false,
    skeletonRows: skeletonRowsProp,
    hidePagination = false,
    footer,
    stickyLastColumn = false,
    stickyHeader = true,
    maxTableHeight = '60vh',
}: DataGridTableProps<TData>) => {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: controlledPageIndex ?? 0,
        pageSize: controlledPageSize,
    });

    const effectivePageIndex = controlledPageIndex ?? pagination.pageIndex;
    const total = totalCount ?? data.length;
    const pageCount = Math.ceil(total / controlledPageSize);

    const table = useReactTable({
        data,
        columns,
        state: { sorting, pagination: { pageIndex: effectivePageIndex, pageSize: controlledPageSize } },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: !!onPageChange,
        pageCount,
    });

    const handlePageChange = (p: number) => {
        if (onPageChange) {
            onPageChange(p);
        } else {
            setPagination(prev => ({ ...prev, pageIndex: p }));
        }
    };

    const skeletonRowCount = skeletonRowsProp ?? controlledPageSize;

    return (
        <div className="bg-[var(--white-color)] rounded-2xl border border-[var(--neutral-color)] overflow-hidden">
            <div
                className={`w-full ${stickyHeader ? 'overflow-auto' : 'overflow-x-auto'}`}
                style={stickyHeader ? { maxHeight: maxTableHeight } : undefined}
            >
                <table className="w-full border-collapse">
                    {/* Header */}
                    <thead className="bg-[var(--primary-canvas-color)] border-b border-[var(--neutral-color)]">
                        {table.getHeaderGroups().map(hg => (
                            <tr key={hg.id}>
                                {hg.headers.map((header, headerIndex) => {
                                    const isLastColumn = headerIndex === hg.headers.length - 1;
                                    const isStickyCell = stickyLastColumn && isLastColumn;
                                    const isActionsColumn = header.column.id === 'actions';
                                    const isStatusColumn = header.column.id === 'is_active';
                                    return (
                                        <th
                                            key={header.id}
                                            onClick={header.column.getToggleSortingHandler()}
                                            className={`py-2.5 2xl:py-4 px-[10px] ${(isActionsColumn || isStatusColumn) ? 'text-center' : 'text-left'} ${isActionsColumn ? 'border-l border-[var(--neutral-color)]' : ''} text-[11px] 2xl:text-xs font-bold text-[#374151] whitespace-nowrap select-none bg-[var(--primary-canvas-color)] ${isStickyCell ? 'sticky right-0 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]' : ''} ${stickyHeader ? 'sticky top-0 z-20' : ''}${isStickyCell && stickyHeader ? ' z-30' : isStickyCell ? ' z-10' : ''}${header.column.getCanSort() ? ' cursor-pointer hover:text-[#374151]' : ''}`}
                                            style={
                                                isStickyCell && header.getSize() !== 150
                                                    ? {
                                                        width: header.getSize(),
                                                        minWidth: header.getSize(),
                                                    }
                                                    : { width: header.getSize() !== 150 ? header.getSize() : undefined }
                                            }
                                        >
                                            <span className={`inline-flex items-center gap-1${(isActionsColumn || isStatusColumn) ? ' justify-center w-full' : ''}`}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {header.column.getCanSort() && (
                                                    <SortIcon direction={header.column.getIsSorted()} />
                                                )}
                                            </span>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    {/* Body */}
                    <tbody className="divide-y divide-[var(--neutral-color)]">
                        {loading ? (
                            Array.from({ length: skeletonRowCount }).map((_, i) => (
                                <tr key={`skeleton-row-${i}`} aria-hidden="true">
                                    <td colSpan={columns.length} className="py-2 2xl:py-3 px-[10px]">
                                        <SkeletonLoader rows={1} className="h-11 2xl:h-12 w-full rounded-lg" />
                                    </td>
                                </tr>
                            ))
                        ) : table.getRowModel().rows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="py-12 px-4 text-center text-[var(--secondary-color)] text-xs 2xl:text-sm">
                                    No records found
                                </td>
                            </tr>
                        ) : (
                            table.getRowModel().rows.map(row => (
                                <tr key={row.id} className="group transition-colors duration-100 hover:bg-[var(--primary-canvas-color)]">
                                    {row.getVisibleCells().map((cell, cellIndex) => {
                                        const isLastColumn = cellIndex === row.getVisibleCells().length - 1;
                                        const isStickyCell = stickyLastColumn && isLastColumn;
                                        const isActionsColumn = cell.column.id === 'actions';
                                        const isStatusColumn = cell.column.id === 'is_active';
                                        const colW = cell.column.getSize();
                                        const stickyWidthStyle =
                                            isStickyCell && colW !== 150
                                                ? { width: colW, minWidth: colW }
                                                : undefined;
                                        return (
                                            <td
                                                key={cell.id}
                                                style={stickyWidthStyle}
                                                className={`py-2 2xl:py-3 px-[10px] text-[11px] 2xl:text-[12px] text-[var(--secondary-color)] whitespace-nowrap [&_a]:text-inherit [&_a]:no-underline ${(isActionsColumn || isStatusColumn) ? 'text-center' : ''} ${isActionsColumn ? 'border-l border-[var(--neutral-color)]' : ''} ${isStickyCell
                                                    ? 'sticky right-0 z-20 bg-[var(--white-color)] group-hover:bg-[var(--primary-canvas-color)] shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]'
                                                    : ''
                                                    }`}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {footer}
            {!hidePagination && loading ? (
                <div
                    className="flex items-center justify-between px-3 2xl:px-4 py-2 2xl:py-3 border-t border-[var(--neutral-color)] gap-4"
                    aria-hidden="true"
                >
                    <SkeletonLoader rows={1} className="h-4 w-44 2xl:w-52 rounded-md" />
                    <div className="flex items-center gap-1 shrink-0">
                        <SkeletonLoader rows={1} className="h-6 2xl:h-8 w-6 2xl:w-8 rounded-[6px] 2xl:rounded-[10px]" />
                        {Array.from({ length: 5 }).map((_, i) => (
                            <SkeletonLoader
                                key={i}
                                rows={1}
                                className="h-6 2xl:h-8 w-6 2xl:w-8 rounded-[6px] 2xl:rounded-[10px]"
                            />
                        ))}
                        <SkeletonLoader rows={1} className="h-6 2xl:h-8 w-6 2xl:w-8 rounded-[6px] 2xl:rounded-[10px]" />
                    </div>
                </div>
            ) : !hidePagination ? (
                <Pagination
                    pageIndex={effectivePageIndex}
                    pageCount={pageCount}
                    totalCount={total}
                    pageSize={controlledPageSize}
                    onPageChange={handlePageChange}
                />
            ) : null}
        </div>
    );
};

export default DataGridTable;
